import { Box, Flex, Image, LabelledCheckbox, Text, TextLink } from '@modusign/design-system';
import { useReducer } from 'react';

import signIcon from '@app/assets/icons/icon-signature-sign-white.svg';
import {
  LATEST_ELECTRONIC_SIGNING_URL,
  LATEST_IN_PERSON_SIGNING_SERVICE,
  LATEST_PRIVACY_URL,
} from '@app/constants/terms';
import useResponsiveValue from '@app/hooks/useResponsiveValue';

import getFormattedParticipantInfo from '../../lib/getFormattedParticiapntInfo';
import InPersonInfoCardLayout from '../InPersonInfoCardLayout';
import { ResponsiveTable, ResponsiveTableCell } from '../ResponsiveTable';
import SectionalBanner from '../SectionalBanner';

type Terms = 'FIRST' | 'SECOND' | 'THIRD';
type Agreement = Record<Terms, boolean>;

export default function ParticipantAgreement({
  documentName,
  participantName,
  participantContact,
  onClickGotoSign,
}: {
  documentName: string;
  participantName: string;
  participantContact: string;
  onClickGotoSign: () => void;
}) {
  const [agreement, dispatchToggleAgreement] = useReducer(
    (s: Agreement, action: { type: Terms; payload: boolean }) => ({
      ...s,
      [action.type]: action.payload,
    }),
    {
      FIRST: false,
      SECOND: false,
      THIRD: false,
    }
  );
  const isMobileWidth = useResponsiveValue([true, false, false]);
  const disableMessage = Object.keys(agreement).every((k) => agreement[k as Terms])
    ? null
    : '안내 사항을 확인하고, 체크박스란에 체크해 주세요.';

  const participantDataRow = [
    {
      title: '문서 제목',
      value: (
        <ResponsiveTableCell>
          <Text>{documentName}</Text>
        </ResponsiveTableCell>
      ),
    },
    {
      title: '서명자',
      value: (
        <ResponsiveTableCell>
          <Text>{getFormattedParticipantInfo(participantName, participantContact)}</Text>
        </ResponsiveTableCell>
      ),
    },
  ];

  return (
    <>
      <Box mb="4">
        <SectionalBanner
          backgroundColor="boxBackgroundEmphasisBase"
          borderColor="boxBorderEmphasisBase"
          borderStyle="solid"
          borderWidth="1px"
          description={`${participantName}님이 서명할 차례입니다.`}
          headerIcon={
            <Flex
              alignItems="center"
              bg="boxBorderEmphasisBase"
              borderRadius="4px"
              fontSize="18px"
              height="48px"
              justifyContent="center"
              width="48px"
            >
              <Image src={signIcon} sx={{ background: 'iconNeutralLightest' }} />
            </Flex>
          }
          title="서명자"
        />
      </Box>
      <InPersonInfoCardLayout
        buttonText="서명 시작하기"
        description="아래 사항을 확인 후, 서명 시작하기 버튼을 눌러주세요. "
        disabledMessage={disableMessage}
        title={`${participantName}님이 서명할 차례입니다.`}
        onFooterButtonClicked={onClickGotoSign}
      >
        <Box mb={2} width="100%">
          <ResponsiveTable dataList={participantDataRow} />
        </Box>

        <Flex flexDirection="column" gap={isMobileWidth ? 4 : 0} mb={isMobileWidth ? 2 : 3} mt={6}>
          <LabelledCheckbox
            checked={agreement.FIRST}
            variant={isMobileWidth ? 'bold' : 'medium'}
            onChange={(e) => dispatchToggleAgreement({ type: 'FIRST', payload: e.target.checked })}
          >
            <Text color="textNeutralSlightlyLight">
              {participantName} 본인이 맞으며 직접 서명에 참여합니다.
            </Text>
          </LabelledCheckbox>
          <LabelledCheckbox
            checked={agreement.SECOND}
            variant={isMobileWidth ? 'bold' : 'medium'}
            onChange={(e) => dispatchToggleAgreement({ type: 'SECOND', payload: e.target.checked })}
          >
            <Text color="textNeutralSlightlyLight">
              <TextLink
                href={LATEST_ELECTRONIC_SIGNING_URL}
                rel="noopener noreferrer"
                target="_blank"
              >
                전자서명 이용약관
              </TextLink>
              과{' '}
              <TextLink href={LATEST_PRIVACY_URL} rel="noopener noreferrer" target="_blank">
                전자서명 개인정보처리방침
              </TextLink>
              에 동의합니다.
            </Text>
          </LabelledCheckbox>
          <LabelledCheckbox
            checked={agreement.THIRD}
            variant={isMobileWidth ? 'bold' : 'medium'}
            onChange={(e) => dispatchToggleAgreement({ type: 'THIRD', payload: e.target.checked })}
          >
            <Text color="textNeutralSlightlyLight">
              <TextLink
                href={LATEST_IN_PERSON_SIGNING_SERVICE}
                rel="noopener noreferrer"
                target="_blank"
              >
                대면서명 이용약관
              </TextLink>
              에 동의합니다.
            </Text>
          </LabelledCheckbox>
        </Flex>
      </InPersonInfoCardLayout>
    </>
  );
}
